/**
 * The network graph on a canvas: drawing, the camera, and what a pointer or a
 * key does.
 *
 * Kept out of React because none of it is React's business. The component
 * hands over an element and the graph; this draws into it and returns a way
 * to take it all down again. The geometry it relies on lives in
 * `networkGraph.layout`, where it can be tested without a canvas.
 */
import {
  cameraZoomBounds,
  clampCameraCenter,
  compositionShape,
  connectedGraph,
  findOrganizationAtScreenPoint,
  findOrganizationLabelAtScreenPoint,
  focusLayoutTargets,
  focusSceneFrame,
  keepNetworkInView,
  linkHash,
  linkStride,
  lineWeight,
  MAX_ZOOM,
  nodeImportance,
  overviewSceneFrame,
  paintForImportance,
  planOrganizationLabels,
  screenNodeRadius,
  type PlacedLabel,
} from "./networkGraph.layout";
import {
  DEFAULT_HOPS,
  copyGraphForSimulation,
  focusScene,
  linkIdentity,
  type Graph,
  type GraphLink,
  type GraphNode,
  type Point,
  type Scene,
} from "./networkGraph.model";
import { drift, moved, phaseOf, ringed, shared, swollen, wander } from "./networkGraph.breathing";
import { watchPixelRatio } from "./networkGraph.pixelRatio";
import { settle } from "./networkGraph.simulation";

/** Give each node its settled place before the canvas first draws it. */
function placed(graph: Graph, places: Map<string, Point>): Graph {
  for (const node of graph.nodes) Object.assign(node, places.get(node.id) ?? { x: 0, y: 0 });
  return graph;
}

const palette = { paper: "#fdfdc1", ink: "#172c00", muted: "#4d5d39", hover: "#359B00" };
// The page's own typeface, by the name its stylesheet registers it under.
const FONT = '"TheFuture", Arial, sans-serif';
const TRANSITION_MS = 650;
const ZOOM_STEP_MS = 220;
/** How much one press of a zoom button changes the scale. */
const ZOOM_STEP = 1.5;
/** Below this width the graph is on a phone: fewer labels, thinner links. */
const COMPACT_WIDTH = 520;
/**
 * The least by which the whole-network view opens closer than a full fit. The
 * edges of the network run off the frame, so it reads as part of something
 * larger than the page shows; zooming out still reaches all of it.
 */
const OVERVIEW_CROP = 1.45;

const OVERVIEW_LABEL =
  "Organization network. Select an organization to focus it. Use arrow keys to move between organizations; press Escape to see the whole network.";

export type NetworkGraphOptions = {
  /** The element the canvas is created in */
  host: HTMLElement;
  /** The element whose size the graph fills, and where the hover label sits */
  frame: HTMLElement;
  /** A positioned element inside the frame, shown beside a hovered organization */
  hoverLabel: HTMLElement;
  graph: Graph;
  isMotionReduced: boolean;
  /** How many links out from a chosen organization its neighbourhood reaches */
  hops?: number;
  /** Told when dragging the graph is taken up or handed back */
  onEngagedChange?: (isEngaged: boolean) => void;
  /** Told when there stops or starts being further to zoom, either way */
  onZoomRangeChange?: (range: { canZoomIn: boolean; canZoomOut: boolean }) => void;
  /** Told once the first frame is on screen */
  onReady?: () => void;
};

export type NetworkGraphHandle = {
  destroy: () => void;
  /** Step the camera one notch closer */
  zoomIn: () => void;
  /** Step the camera one notch further out */
  zoomOut: () => void;
  /** Hand dragging back to the page */
  release: () => void;
};

type Transition = {
  fromNodes: Map<string, number>;
  fromLinks: Map<string, number>;
  fromPositions: Map<string, Point>;
  targets: Map<string, Point>;
  progress: number;
};

export async function mountNetworkGraph({
  host,
  frame,
  hoverLabel,
  graph,
  isMotionReduced,
  hops = DEFAULT_HOPS,
  onEngagedChange,
  onZoomRangeChange,
  onReady,
}: NetworkGraphOptions): Promise<NetworkGraphHandle> {
  const connected = connectedGraph(graph);
  // The library is fetched while the layout is being worked out elsewhere.
  const [{ default: ForceGraph }, settled] = await Promise.all([
    import("force-graph"),
    settle(connected),
  ]);
  const organizationNames = new Map(
    connected.nodes.filter((node) => node.type === "org").map((node) => [node.id, node.name ?? ""])
  );
  const organizationDegree = new Map([...organizationNames.keys()].map((key) => [key, 0]));
  for (const link of connected.links) {
    for (const end of [link.source, link.target]) {
      const id = typeof end === "string" ? end : end.id;
      if (organizationDegree.has(id)) organizationDegree.set(id, organizationDegree.get(id)! + 1);
    }
  }

  // Whose ring each person is drawn in: those who belong to one organization.
  // Anyone in several is carried by all of them when they move.
  const ringOf = new Map<string, string>();
  const organizationsOf = new Map<string, Set<string>>();
  {
    for (const link of connected.links) {
      const source = typeof link.source === "string" ? link.source : link.source.id;
      const target = typeof link.target === "string" ? link.target : link.target.id;
      const organization = organizationNames.has(source)
        ? source
        : organizationNames.has(target)
          ? target
          : null;
      if (!organization) continue;
      const person = organization === source ? target : source;
      if (organizationNames.has(person)) continue;
      if (!organizationsOf.has(person)) organizationsOf.set(person, new Set());
      organizationsOf.get(person)!.add(organization);
    }
    for (const [person, organizations] of organizationsOf) {
      if (organizations.size === 1) ringOf.set(person, [...organizations][0]);
    }
  }
  const phases = new Map(connected.nodes.map((node) => [node.id, phaseOf(node.id)]));

  const state = {
    scene: focusScene(connected, "", hops) as Scene,
    display: placed(copyGraphForSimulation(connected), settled),
    pendingFit: true,
    hoverId: "",
    transition: null as Transition | null,
    fadeFrame: 0,
    baseline: null as Map<string, Point> | null,
    visibleLabels: [] as PlacedLabel[],
    shapeKey: "",
    overviewAnchor: null as Point | null,
    cameraLimits: null as { min: number; max: number } | null,
    // Where each node sits before breathing moves it: its resting place, or
    // somewhere on the way to it while the view is changing. What is drawn is
    // always this plus the breathing, so nothing shifts when a change ends.
    rest: new Map<string, Point>(),
    isOnScreen: true,
    breathingFrame: 0,
    // Whether a drag belongs to the graph or to the page.
    isEngaged: false,
    isDestroyed: false,
  };

  const isCompact = () => frame.clientWidth < COMPACT_WIDTH;
  const currentShape = () => compositionShape(frame.clientWidth, frame.clientHeight);

  function currentNodeImportance(node: GraphNode) {
    const target = nodeImportance(node, state.scene, state.hoverId);
    if (!state.transition) return target;
    const from = state.transition.fromNodes.get(node.id) ?? 1;
    return from + (target - from) * state.transition.progress;
  }

  // Every link is asked about on every frame, so what never changes about
  // it is worked out once.
  const linkFacts = new WeakMap<object, { id: string; hash: number }>();
  function factsOf(link: GraphLink) {
    let facts = linkFacts.get(link);
    if (!facts) {
      const id = linkIdentity(link);
      facts = { id, hash: linkHash(id) };
      linkFacts.set(link, facts);
    }
    return facts;
  }
  const isLinkActive = (id: string) =>
    Boolean(state.scene.focusId) && state.scene.activeLinkIds.has(id);

  function currentLinkImportance(link: GraphLink) {
    const { id } = factsOf(link);
    const target = !state.scene.focusId || state.scene.activeLinkIds.has(id) ? 1 : 0;
    if (!state.transition) return target;
    const from = state.transition.fromLinks.get(id) ?? 1;
    return from + (target - from) * state.transition.progress;
  }

  function isLinkDrawn(link: GraphLink) {
    const { id, hash } = factsOf(link);
    const stride = linkStride(isLinkActive(id), state.scene, network.zoom(), isCompact());
    return stride === 1 || hash % stride === 0;
  }

  // The nodes are drawn here in one pass per frame instead of one call each.
  // There are thousands of people and they share a handful of looks, so each
  // look is a single path filled once, and anybody outside the frame is
  // skipped. This is most of what makes a frame cheap enough to animate.
  function drawNodes(context: CanvasRenderingContext2D, scale: number) {
    const isFocused = Boolean(state.scene.focusId);
    const topLeft = network.screen2GraphCoords(0, 0);
    const bottomRight = network.screen2GraphCoords(frame.clientWidth, frame.clientHeight);
    const margin = 12 / scale;
    const isInView = (x: number, y: number) =>
      x >= topLeft.x - margin &&
      x <= bottomRight.x + margin &&
      y >= topLeft.y - margin &&
      y <= bottomRight.y + margin;

    const quiet = screenNodeRadius("person", false, scale);
    const active = screenNodeRadius("person", true, scale);
    const looks = new Map<number, GraphNode[]>();
    const organizations: GraphNode[] = [];
    for (const node of state.display.nodes) {
      const x = node.x ?? 0;
      const y = node.y ?? 0;
      if (!isInView(x, y)) continue;
      if (node.type === "org") {
        organizations.push(node);
        continue;
      }
      const importance = Math.round(currentNodeImportance(node) * 20) / 20;
      const alike = looks.get(importance);
      if (alike) alike.push(node);
      else looks.set(importance, [node]);
    }

    for (const [importance, people] of looks) {
      const radius = (quiet + (active - quiet) * importance) / scale;
      context.beginPath();
      for (const node of people) {
        const x = node.x ?? 0;
        const y = node.y ?? 0;
        context.moveTo(x + radius, y);
        context.arc(x, y, radius, 0, 2 * Math.PI);
      }
      context.fillStyle = paintForImportance("person", importance, isFocused);
      context.fill();
    }

    for (const node of organizations) {
      const selected = node.id === state.scene.focusId;
      context.beginPath();
      context.arc(
        node.x ?? 0,
        node.y ?? 0,
        screenNodeRadius("org", selected, scale) / scale,
        0,
        2 * Math.PI
      );
      context.fillStyle =
        node.id === state.hoverId
          ? palette.hover
          : selected
            ? palette.ink
            : paintForImportance("org", currentNodeImportance(node), isFocused);
      context.fill();
    }
  }

  function drawLabels(context: CanvasRenderingContext2D, scale: number) {
    const organizations = state.display.nodes.filter(
      (node) =>
        node.type === "org" &&
        (!state.scene.focusId ||
          state.scene.activeNodeIds.has(node.id) ||
          node.id === state.hoverId)
    );
    const labels = planOrganizationLabels(
      organizations.map((node) => ({ ...node, degree: organizationDegree.get(node.id) || 0 })),
      {
        focusId: state.scene.focusId,
        width: frame.clientWidth,
        height: frame.clientHeight,
        compact: isCompact(),
        zoom: scale,
        project: (node) => network.graph2ScreenCoords(node.x, node.y),
        measure: (text, selected) => {
          context.font = `${selected ? 700 : 500} ${selected ? 15 : 13}px ${FONT}`;
          return context.measureText(text).width;
        },
      }
    );
    state.visibleLabels = labels;
    for (const label of labels) {
      context.save();
      context.translate(label.node.x + label.dx / scale, label.node.y + label.dy / scale);
      context.scale(1 / scale, 1 / scale);
      context.font = `${label.selected ? 700 : 500} ${label.fontSize}px ${FONT}`;
      context.textAlign = label.align;
      context.textBaseline = "middle";
      context.lineWidth = label.selected ? 6 : 5;
      context.lineJoin = "round";
      context.strokeStyle = palette.paper;
      context.strokeText(label.text, 0, 0);
      context.fillStyle = label.selected ? palette.ink : palette.muted;
      context.fillText(label.text, 0, 0);
      context.restore();
    }
  }

  let isRestrictingCamera = false;
  function keepCameraInBounds() {
    if (!state.baseline || !state.overviewAnchor || state.transition || isRestrictingCamera) return;
    const center = network.centerAt();
    const boxed = clampCameraCenter(
      state.display.nodes,
      center,
      network.zoom(),
      frame.clientWidth,
      frame.clientHeight,
      { focusId: state.scene.focusId, anchor: state.overviewAnchor }
    );
    // A selected organization is centred wherever it sits, the rim of the
    // network included, so only the whole-network view is held over the middle.
    const bounded = state.scene.focusId
      ? boxed
      : keepNetworkInView(
          state.display.nodes,
          boxed,
          network.zoom(),
          frame.clientWidth,
          frame.clientHeight
        );
    if (Math.abs(bounded.x - center.x) < 0.25 && Math.abs(bounded.y - center.y) < 0.25) return;
    isRestrictingCamera = true;
    network.centerAt(bounded.x, bounded.y, 0);
    isRestrictingCamera = false;
  }

  // Whether there is any further to zoom each way, so the buttons can say so.
  let zoomRange = "";
  function reportZoomRange() {
    if (!state.cameraLimits) return;
    const zoom = network.zoom();
    const range = {
      canZoomIn: zoom < state.cameraLimits.max * 0.995,
      canZoomOut: zoom > state.cameraLimits.min * 1.005,
    };
    const key = `${range.canZoomIn}:${range.canZoomOut}`;
    if (key === zoomRange) return;
    zoomRange = key;
    onZoomRangeChange?.(range);
  }

  function zoomBy(factor: number) {
    if (!state.cameraLimits || state.transition) return;
    const next = Math.max(
      state.cameraLimits.min,
      Math.min(state.cameraLimits.max, network.zoom() * factor)
    );
    network.zoom(next, isMotionReduced ? 0 : ZOOM_STEP_MS);
  }

  function updateCameraLimits() {
    if (!state.baseline) return;
    const overview = focusScene(connected, "", hops);
    const targets = focusLayoutTargets(
      state.display.nodes,
      overview,
      state.baseline,
      state.display.links,
      currentShape()
    );
    const nodes = state.display.nodes.map((node) => ({ ...node, ...targets.get(node.id) }));
    const overviewFrame = overviewSceneFrame(nodes, frame.clientWidth, frame.clientHeight);
    if (!overviewFrame) return;
    state.overviewAnchor = { x: overviewFrame.x, y: overviewFrame.y };
    state.cameraLimits = cameraZoomBounds(overviewFrame.zoom);
    network.minZoom(state.cameraLimits.min).maxZoom(state.cameraLimits.max);
    reportZoomRange();
  }

  // force-graph's own types describe nodes and links loosely; the accessors
  // below are only ever called with the nodes and links given to it here.
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const network = new ForceGraph(host);
  network
    .width(frame.clientWidth)
    .height(frame.clientHeight)
    .backgroundColor(palette.paper)
    .nodeId("id")
    .nodeLabel(() => "")
    // Nodes and labels are drawn together after the links, and the library's
    // own hit-testing canvas is not needed: pointers are resolved here against
    // the live positions.
    .nodeCanvasObject(() => {})
    .enablePointerInteraction(false)
    .onRenderFramePost((context, scale) => {
      drawNodes(context, scale);
      drawLabels(context, scale);
    })
    .linkColor((link: any) =>
      paintForImportance("link", currentLinkImportance(link), Boolean(state.scene.focusId))
    )
    .linkVisibility((link: any) => isLinkDrawn(link))
    .linkWidth((link: any) => lineWeight(link, network.zoom()))
    .linkDirectionalArrowLength(0)
    .onZoom(() => {
      keepCameraInBounds();
      reportZoomRange();
    })
    .maxZoom(MAX_ZOOM)
    // Every node arrives already placed, so the library's own simulation
    // has nothing to do and stops on its first frame.
    .warmupTicks(0)
    .cooldownTicks(0)
    .enableNodeDrag(false)
    // The graph sits in the middle of a long page, so the wheel always
    // scrolls the page and never zooms the graph; zooming is by the buttons
    // on the frame, or a pinch. A drag scrolls the page too until somebody
    // has chosen to explore the graph.
    .enableZoomInteraction(false)
    .enablePanInteraction(() => state.isEngaged)
    .onEngineStop(() => {
      if (state.isDestroyed) return;
      if (!state.baseline) {
        state.baseline = new Map(
          state.display.nodes.map((node) => [node.id, { x: node.x ?? 0, y: node.y ?? 0 }])
        );
        const shape = currentShape();
        state.shapeKey = `${shape.x}:${shape.y}`;
        const grouped = focusLayoutTargets(
          state.display.nodes,
          state.scene,
          state.baseline,
          state.display.links,
          shape
        );
        for (const node of state.display.nodes) Object.assign(node, grouped.get(node.id));
        state.rest = new Map(grouped);
        updateCameraLimits();
      }
      if (state.pendingFit) {
        state.pendingFit = false;
        fitGraph(undefined, 0);
        onReady?.();
        breathe();
      }
    });

  network.graphData(state.display as any);
  /* eslint-enable @typescript-eslint/no-explicit-any */

  const canvas = host.querySelector("canvas")!;
  canvas.tabIndex = 0;
  canvas.setAttribute("aria-label", OVERVIEW_LABEL);
  canvas.style.touchAction = "pan-y";
  canvas.style.outlineOffset = "-3px";

  function setEngaged(isEngaged: boolean) {
    if (state.isEngaged === isEngaged) return;
    state.isEngaged = isEngaged;
    canvas.style.touchAction = isEngaged ? "none" : "pan-y";
    onEngagedChange?.(isEngaged);
  }

  // The canvas only needs redrawing while something on it is moving.
  const isBreathing = () =>
    !isMotionReduced && state.isOnScreen && !document.hidden && !state.isDestroyed;
  function restRedraw() {
    if (state.isDestroyed || state.transition) return;
    network.autoPauseRedraw(true);
  }

  // While idle, every node moves a little around where it rests. A change of
  // focus takes the nodes over for its own travel and hands them back after.
  //
  // The motion is slow, so it does not need many frames, and each frame means
  // redrawing the whole canvas. It is drawn about thirty times a second where
  // that is cheap, and paces itself by what a frame turns out to cost, so a
  // slow phone gets a gentler rhythm instead of a busy page.
  const BREATH_MS = { quickest: 33, slowest: 800 };
  /** Drawing may take about a sixth of the time; the rest is the page's. */
  const BREATH_SHARE = 6;
  function breathe() {
    cancelAnimationFrame(state.breathingFrame);
    if (!isBreathing()) {
      restRedraw();
      return;
    }
    let interval = BREATH_MS.quickest;
    let drawnAt = 0;
    let previous = 0;
    // The canvas is redrawn on the frame after it is asked to be, so the cost
    // of a draw shows in the two frames that follow the request.
    let framesSinceDraw = 3;
    let cost = 0;
    const step = (now: number) => {
      if (!isBreathing()) {
        restRedraw();
        return;
      }
      if (previous && framesSinceDraw < 2) {
        cost = Math.max(cost, now - previous);
        framesSinceDraw += 1;
        if (framesSinceDraw === 2) {
          const wanted = cost * BREATH_SHARE;
          // Eased towards, so one slow frame does not set the pace by itself.
          interval = Math.min(
            BREATH_MS.slowest,
            Math.max(BREATH_MS.quickest, interval + (wanted - interval) * 0.5)
          );
        }
      }
      previous = now;
      if (
        framesSinceDraw >= 2 &&
        !state.transition &&
        state.rest.size &&
        now - drawnAt >= interval
      ) {
        drawnAt = now;
        framesSinceDraw = 0;
        cost = 0;
        place(now / 1000);
        requestGraphPaint();
      }
      state.breathingFrame = requestAnimationFrame(step);
    };
    state.breathingFrame = requestAnimationFrame(step);
  }

  function place(seconds: number) {
    const organizations = state.display.nodes.filter((node) => node.type === "org");
    const rests = organizations
      .map((node) => state.rest.get(node.id))
      .filter((rest): rest is Point => Boolean(rest));
    if (!rests.length) return;
    // The whole network eases outward and back about its middle.
    const centre = {
      x: rests.reduce((sum, rest) => sum + rest.x, 0) / rests.length,
      y: rests.reduce((sum, rest) => sum + rest.y, 0) / rests.length,
    };

    // Organizations wander, each on its own path.
    const movement = new Map<string, Point>();
    const organizationNow = new Map<string, Point>();
    for (const node of organizations) {
      const rest = state.rest.get(node.id);
      if (!rest) continue;
      const by = wander(phases.get(node.id) ?? 0, seconds);
      const spot = swollen(moved(rest, by), centre, seconds);
      movement.set(node.id, { x: spot.x - rest.x, y: spot.y - rest.y });
      organizationNow.set(node.id, spot);
      node.x = spot.x;
      node.y = spot.y;
    }

    for (const node of state.display.nodes) {
      if (node.type === "org") continue;
      const rest = state.rest.get(node.id);
      if (!rest) continue;
      const ring = ringOf.get(node.id);
      const ringRest = ring ? state.rest.get(ring) : undefined;
      let spot: Point;
      if (ring && ringRest) {
        // In a ring: carried with its organization, breathing in and out.
        spot = ringed(
          rest,
          ringRest,
          organizationNow.get(ring) ?? ringRest,
          phases.get(ring) ?? 0,
          seconds
        );
      } else {
        // Between organizations: carried by all of them at once, with a
        // little movement of their own. With none, just eased with the rest.
        const carriers = [...(organizationsOf.get(node.id) ?? [])]
          .map((id) => movement.get(id))
          .filter((by): by is Point => Boolean(by));
        const own = drift(phases.get(node.id) ?? 0, seconds);
        spot = carriers.length
          ? moved(moved(rest, shared(carriers)), own)
          : swollen(moved(rest, own), centre, seconds);
      }
      node.x = spot.x;
      node.y = spot.y;
    }
  }

  function requestGraphPaint() {
    if (state.transition) return;
    network.autoPauseRedraw(false);
    requestAnimationFrame(() => {
      restRedraw();
    });
  }

  // force-graph throttles its own hit-testing while positions animate, so the
  // live coordinates are read here and a dimmed organization never has a
  // stale click target after a change of focus.
  function organizationAt(event: PointerEvent) {
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const label = findOrganizationLabelAtScreenPoint(state.visibleLabels, x, y);
    if (label) return { node: label.node as GraphNode, viaLabel: true };
    const node = findOrganizationAtScreenPoint(
      state.display.nodes,
      (candidate) => network.graph2ScreenCoords(candidate.x, candidate.y),
      x,
      y
    );
    return node ? { node, viaLabel: false } : null;
  }

  // The whole-network view fills the frame from edge to edge, as the picture
  // it replaces does: the network is round and the frame is wide, so it is
  // zoomed until the organizations reach both sides and the top and bottom
  // run off. In a frame that is not wide it is still opened a little closer
  // than a full fit.
  const cropped = <T extends Point & { zoom: number }>(
    view: T | null,
    nodes: GraphNode[]
  ): T | null => {
    if (!view) return view;
    const organizations = nodes
      .filter((node) => node.type === "org")
      .map((node) => ({ x: node.x ?? 0, y: node.y ?? 0 }));
    const reach = (axis: "x" | "y") =>
      Math.max(1, ...organizations.map((spot) => Math.abs(spot[axis] - view[axis])));
    const cover = Math.max(
      frame.clientWidth / 2 / reach("x"),
      frame.clientHeight / 2 / reach("y")
    );
    return {
      ...view,
      zoom: Math.min(state.cameraLimits?.max ?? MAX_ZOOM, Math.max(view.zoom * OVERVIEW_CROP, cover)),
    };
  };

  function fitGraph(
    targets: Map<string, Point> | undefined = state.transition?.targets,
    duration = isMotionReduced ? 0 : TRANSITION_MS
  ) {
    const places = targets ?? state.rest;
    const framedNodes = state.display.nodes.map((node) => ({ ...node, ...places.get(node.id) }));
    const padding = isCompact() ? { x: 28, y: 36 } : { x: 72, y: 60 };
    const view = state.scene.focusId
      ? focusSceneFrame(
          framedNodes,
          state.scene,
          frame.clientWidth,
          frame.clientHeight,
          padding,
          state.display.links
        )
      : cropped(
          overviewSceneFrame(framedNodes, frame.clientWidth, frame.clientHeight, padding),
          framedNodes
        );
    if (!view) {
      network.zoomToFit(duration, 48);
      return;
    }
    network.centerAt(view.x, view.y, duration);
    network.zoom(
      Math.max(state.cameraLimits?.min ?? 0.12, Math.min(state.cameraLimits?.max ?? MAX_ZOOM, view.zoom)),
      duration
    );
  }

  function setFocus(key: string) {
    if (!state.baseline) return;
    const focusId = organizationNames.has(key) ? key : "";
    if (state.scene.focusId === focusId) return;
    state.hoverId = "";
    hoverLabel.hidden = true;
    canvas.setAttribute(
      "aria-label",
      focusId
        ? `Network focused on ${organizationNames.get(focusId)}. Select another organization or press Escape to see the whole network.`
        : OVERVIEW_LABEL
    );
    const fromNodes = new Map(
      state.display.nodes.map((node) => [node.id, currentNodeImportance(node)])
    );
    const fromLinks = new Map(
      state.display.links.map((link) => [linkIdentity(link), currentLinkImportance(link)])
    );
    // Travel starts from where each node sits before breathing, not from
    // where breathing has it this instant, so the two never fight.
    const fromPositions = new Map(
      state.display.nodes.map((node) => [
        node.id,
        { ...(state.rest.get(node.id) ?? { x: node.x ?? 0, y: node.y ?? 0 }) },
      ])
    );
    state.scene = focusScene(connected, focusId, hops);
    const targets = focusLayoutTargets(
      state.display.nodes,
      state.scene,
      state.baseline,
      state.display.links,
      currentShape()
    );
    cancelAnimationFrame(state.fadeFrame);
    if (isMotionReduced) {
      state.rest = new Map(targets);
      for (const node of state.display.nodes) Object.assign(node, targets.get(node.id));
      state.transition = null;
      requestGraphPaint();
    } else {
      const transition: Transition = { fromNodes, fromLinks, fromPositions, targets, progress: 0 };
      state.transition = transition;
      network.autoPauseRedraw(false);
      const start = performance.now();
      const advance = (now: number) => {
        if (state.transition !== transition || state.isDestroyed) return;
        const elapsed = Math.min(1, (now - start) / TRANSITION_MS);
        transition.progress = 1 - (1 - elapsed) ** 3;
        for (const node of state.display.nodes) {
          const from = fromPositions.get(node.id)!;
          const to = targets.get(node.id)!;
          state.rest.set(node.id, {
            x: from.x + (to.x - from.x) * transition.progress,
            y: from.y + (to.y - from.y) * transition.progress,
          });
        }
        // Breathing carries on through the change, laid over the travel, so
        // there is nothing to snap back to when the travel is over.
        if (isBreathing()) place(now / 1000);
        else for (const node of state.display.nodes) Object.assign(node, state.rest.get(node.id));
        if (elapsed < 1) {
          state.fadeFrame = requestAnimationFrame(advance);
        } else {
          state.transition = null;
          state.fadeFrame = 0;
          requestAnimationFrame(() => {
            restRedraw();
          });
        }
      };
      state.fadeFrame = requestAnimationFrame(advance);
    }
    fitGraph(targets);
  }

  let pointerDown: Point | null = null;
  const onPointerDown = (event: PointerEvent) => {
    if (event.button === 0) pointerDown = { x: event.clientX, y: event.clientY };
  };
  const onPointerUp = (event: PointerEvent) => {
    if (!pointerDown || event.button !== 0) return;
    const moved = Math.hypot(event.clientX - pointerDown.x, event.clientY - pointerDown.y);
    pointerDown = null;
    if (moved > 6) return;
    // A click inside is what takes up the drag; choosing an
    // organization focuses it, and empty space returns to the whole network.
    setEngaged(true);
    setFocus(organizationAt(event)?.node.id || "");
  };
  const onPointerMove = (event: PointerEvent) => {
    const hit = organizationAt(event);
    const organization = hit?.node;
    const nextHoverId = organization?.id || "";
    if (state.hoverId !== nextHoverId) {
      state.hoverId = nextHoverId;
      requestGraphPaint();
    }
    canvas.style.cursor = organization ? "pointer" : state.isEngaged ? "grab" : "default";
    hoverLabel.hidden =
      !organization || Boolean(hit?.viaLabel) || organization.id === state.scene.focusId;
    if (!hoverLabel.hidden && organization) {
      hoverLabel.textContent = organization.name ?? "";
      const rect = frame.getBoundingClientRect();
      hoverLabel.style.left = `${event.clientX - rect.left + 14}px`;
      hoverLabel.style.top = `${event.clientY - rect.top + 14}px`;
    }
  };
  const onPointerLeave = () => {
    state.hoverId = "";
    pointerDown = null;
    hoverLabel.hidden = true;
    requestGraphPaint();
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      setFocus("");
      setEngaged(false);
      return;
    }
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    event.preventDefault();
    const organizations = state.display.nodes
      .filter((node) => node.type === "org")
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    if (!organizations.length) return;
    const current = organizations.findIndex((node) => node.id === state.scene.focusId);
    const step = event.key === "ArrowRight" ? 1 : -1;
    const next = (current + step + organizations.length) % organizations.length;
    setFocus(organizations[next].id);
  };
  // Anything pressed outside the graph hands scrolling back to the page.
  const onDocumentPointerDown = (event: PointerEvent) => {
    if (!frame.contains(event.target as Node)) setEngaged(false);
  };

  canvas.addEventListener("pointerdown", onPointerDown);
  canvas.addEventListener("pointerup", onPointerUp);
  canvas.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("pointerleave", onPointerLeave);
  canvas.addEventListener("keydown", onKeyDown);
  document.addEventListener("pointerdown", onDocumentPointerDown);

  // A canvas built at one pixel ratio is cleared at whatever the ratio is now,
  // so a ratio that changes under it leaves a band nobody wipes; see
  // networkGraph.pixelRatio. Applying the size again has the backing store
  // rebuilt at the ratio now.
  const stopWatchingRatio = watchPixelRatio(() => {
    if (state.isDestroyed) return;
    // Applying the size also recentres the view, by measuring the backing
    // store against the pixel ratio, and across a change of ratio those two
    // describe different things and the answer comes out wrong. So the camera
    // is put back as it was: a reader who had zoomed in or panned keeps what
    // they were looking at, and sees only the band clear up.
    const center = network.centerAt();
    const zoom = network.zoom();
    network.width(frame.clientWidth).height(frame.clientHeight);
    network.centerAt(center.x, center.y, 0);
    network.zoom(zoom, 0);
    requestGraphPaint();
  });

  const resizeObserver = new ResizeObserver(() => {
    const width = frame.clientWidth;
    const height = frame.clientHeight;
    if (!width || !height || state.isDestroyed) return;
    network.width(width).height(height);
    const shape = currentShape();
    const shapeKey = `${shape.x}:${shape.y}`;
    if (state.baseline && shapeKey !== state.shapeKey) {
      state.shapeKey = shapeKey;
      cancelAnimationFrame(state.fadeFrame);
      state.transition = null;
      const targets = focusLayoutTargets(
        state.display.nodes,
        state.scene,
        state.baseline,
        state.display.links,
        shape
      );
      for (const node of state.display.nodes) Object.assign(node, targets.get(node.id));
      state.rest = new Map(targets);
      requestGraphPaint();
    }
    if (state.baseline) updateCameraLimits();
    if (!state.pendingFit) fitGraph();
  });
  resizeObserver.observe(frame);

  // Nothing moves while the graph is off screen or the tab is in the
  // background, so an idle page costs nothing.
  const visibilityObserver =
    typeof IntersectionObserver === "undefined"
      ? null
      : new IntersectionObserver((entries) => {
          state.isOnScreen = entries.some((entry) => entry.isIntersecting);
          breathe();
        });
  visibilityObserver?.observe(frame);
  const onVisibilityChange = () => breathe();
  document.addEventListener("visibilitychange", onVisibilityChange);

  return {
    zoomIn: () => zoomBy(ZOOM_STEP),
    zoomOut: () => zoomBy(1 / ZOOM_STEP),
    release: () => setEngaged(false),
    destroy: () => {
      state.isDestroyed = true;
      cancelAnimationFrame(state.fadeFrame);
      cancelAnimationFrame(state.breathingFrame);
      resizeObserver.disconnect();
      stopWatchingRatio();
      visibilityObserver?.disconnect();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      document.removeEventListener("pointerdown", onDocumentPointerDown);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerleave", onPointerLeave);
      canvas.removeEventListener("keydown", onKeyDown);
      network.pauseAnimation();
      network._destructor();
      host.replaceChildren();
    },
  };
}
